import { type FC, useMemo, useState } from "react";
import { Home } from "lucide-react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";
import { Badge } from "@/shared/components/ui/badge";
import { CurrencyInput } from "@/shared/components/ui/CurrencyInput";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import { supabase } from "@/shared/lib/supabase";
import { uploadSupportingDocument } from "@/shared/utils/uploadSupportingDocument";
import { currencyInput } from "@/shared/utils/currencyInput";
import { sentryUtils } from "@/shared/utils/sentry";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

interface HomeownersInsuranceFormValues {
  insuranceCompany: string;
  annualPremium: string;
  policyNumber: string;
  policyFile: File | null;
}

interface PropertyHomeownersInsuranceProps extends FlowCustomComponentProps {
  propertyId: number;
}

export const PropertyHomeownersInsurance: FC<PropertyHomeownersInsuranceProps> = ({
  propertyId,
  answers,
  setAnswer,
  onComplete,
}) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { assets, debts, addItem, updateItem, isAddingItem, isUpdatingItem } =
    useEstateItems(estateId);

  const property = useMemo(() => assets.find((p) => p.id === propertyId), [assets, propertyId]);

  const existingPolicy = useMemo(
    () =>
      debts.find((d) => d.linked_item_id === propertyId && d.item_type === "Homeowners insurance"),
    [debts, propertyId],
  );

  const prefix = `insurance_${propertyId}_`;
  const savedCompany = answers[`${prefix}company`];
  const savedPremium = answers[`${prefix}premium`];
  const savedPolicy = answers[`${prefix}policy`];
  const initialForm: HomeownersInsuranceFormValues = {
    insuranceCompany: typeof savedCompany === "string" ? savedCompany : "",
    annualPremium: typeof savedPremium === "string" ? savedPremium : "",
    policyNumber: typeof savedPolicy === "string" ? savedPolicy : "",
    policyFile: null,
  };

  const { form, updateForm } = useFormWithErrors<
    HomeownersInsuranceFormValues,
    Record<string, never>
  >(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const syncToAnswers = (updated: HomeownersInsuranceFormValues) => {
    setAnswer(`${prefix}company`, updated.insuranceCompany);
    setAnswer(`${prefix}premium`, updated.annualPremium);
    setAnswer(`${prefix}policy`, updated.policyNumber);
  };

  const handleFormChange = (updated: HomeownersInsuranceFormValues) => {
    updateForm(updated);
    syncToAnswers(updated);
  };

  const annualPremiumInput = currencyInput(form.annualPremium, (val) =>
    handleFormChange({ ...form, annualPremium: val }),
  );

  const handleSubmit = async () => {
    if (isSubmitting || !estateId) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let policyDocId: number | undefined;

      if (form.policyFile) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) throw new Error("Not authenticated");
        policyDocId = await uploadSupportingDocument(
          form.policyFile,
          estateId,
          session.access_token,
          "home_insurance_policy",
        );
      }

      const itemData = {
        category: "Ongoing Estate Expenses",
        classification: "debt" as const,
        item_type: "Homeowners insurance",
        item_name: form.insuranceCompany.trim() || "Homeowners insurance",
        balance: parseFloat(form.annualPremium) || 0,
        metadata: {
          insurance_company: form.insuranceCompany.trim() || undefined,
          policy_number: form.policyNumber.trim() || undefined,
          annual_premium: parseFloat(form.annualPremium) || undefined,
        },
        source_document_id: policyDocId,
        linked_item_id: propertyId,
      };

      if (existingPolicy) {
        await updateItem(existingPolicy.id, itemData);
      } else {
        await addItem({ estate_id: estateId, ...itemData });
      }

      onComplete();
    } catch (err) {
      setError("Failed to save insurance policy. Please try again.");
      sentryUtils.captureError(err, "PropertyHomeownersInsurance submission failed", {
        estateId: person?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col">
      {property && (
        <div className="mb-6 flex justify-center">
          <Badge variant="outline" className="gap-1.5 border-none bg-white py-1.5 text-secondary">
            <Home className="h-3.5 w-3.5 text-purple-600" aria-hidden="true" />
            {property.item_name}
          </Badge>
        </div>
      )}

      <h2 className="text-center">Add the homeowners insurance policy</h2>
      <p className="mt-2 text-center">
        This helps us track coverage on the home. Add what you have, or come back to it later.
      </p>

      <div className="mt-8">
        <AddItemLayout
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting || isAddingItem || isUpdatingItem}
          error={error}
          onSkip={onComplete}
          skipLabel="I'll add the details later"
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>Insurance company</Label>
              <Input
                value={form.insuranceCompany}
                onChange={(e) => handleFormChange({ ...form, insuranceCompany: e.target.value })}
                placeholder="e.g., State Farm, Allstate, Liberty Mutual"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>
                  Annual premium <span className="text-tertiary">(optional)</span>
                </Label>
                <CurrencyInput
                  value={annualPremiumInput.displayValue}
                  onChange={annualPremiumInput.handleChange}
                  placeholder="0.00"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label>
                  Policy number <span className="text-tertiary">(optional)</span>
                </Label>
                <Input
                  value={form.policyNumber}
                  onChange={(e) => handleFormChange({ ...form, policyNumber: e.target.value })}
                  placeholder="e.g., HO-1234567"
                />
              </div>
            </div>

            <UploadZone
              label="Policy document"
              labelSuffix="(optional)"
              file={form.policyFile}
              onFileSelect={(file) => updateForm({ ...form, policyFile: file })}
              onFileRemove={() => updateForm({ ...form, policyFile: null })}
            />
          </div>
        </AddItemLayout>
      </div>
    </div>
  );
};
